import { applyCommand, type Command } from "../document/commands.ts";
import { seamHead, seamOf, type SeamHead } from "../document/layout-commands.ts";
import { checkSeam } from "../document/layout-rules.ts";
import { CROSSING_KINDS, SEAM_STATES, canonicalPlace, depthOf, neighboursOf, seamKey, seamsOf } from "../document/layout.ts";
import { sameJson } from "../document/same-json.ts";
import type { MapDocument, MapGroup, MapPlace, Seam, SeamCrossing, SeamState, WorldDocument, WorldGrid } from "../document/types.ts";
import { allocateMapId, mapNameRefusal, newMapDocument } from "./map-edits.ts";

/**
 * The layout plan (ADR-109 decision 14): a world's layout as plain data, in Daedalus's own small shape, and
 * `planLayout`, the one pure function that turns a plan and a world into the commands that bring the world to
 * the plan and a report of what they do. A plan names a map by its name, which a plan keeps unique; a seam names
 * its two maps by those names. Nothing here reads or writes a layer, a prop, a marker or a tileset, and nothing
 * names any game.
 */

/**
 * One map of a plan: where it stands, its name, its group, the id of the world's map it is when the plan says
 * so, and `confirmed` when the plan means to move a map that holds layers.
 */
export interface PlanMap {
  readonly place: { readonly x: number; readonly y: number };
  readonly name: string;
  readonly group?: string;
  readonly map?: string;
  readonly confirmed?: true;
}

/**
 * One seam of a plan: its two maps by name, `a` the northern or western, its state, its barrier and its
 * crossings.
 */
export interface PlanSeam {
  readonly a: string;
  readonly b: string;
  readonly state: SeamState;
  readonly barrier?: string;
  readonly crossings: readonly SeamCrossing[];
}

/**
 * A layout plan: the grid, the groups, the maps and the seams.
 */
export interface LayoutPlan {
  readonly grid: WorldGrid;
  readonly groups: readonly MapGroup[];
  readonly maps: readonly PlanMap[];
  readonly seams: readonly PlanSeam[];
}

/**
 * Options of `planLayout`: the tag the editing tab mints its map ids under, or none.
 */
export interface PlanOptions {
  readonly idTag?: string | null;
}

/**
 * A map the plan puts on the grid: its id and name, where it stood (absent when it had no place) and where it
 * stands now; `moved` when it had another place, with the ids of the neighbours it leaves and gains.
 */
export interface PlacedReport {
  readonly id: string;
  readonly name: string;
  readonly from?: MapPlace;
  readonly to: MapPlace;
  readonly moved: boolean;
  readonly leaves: readonly string[];
  readonly gains: readonly string[];
}

/**
 * What a plan's commands do, as data for the editor to word: the maps added, the maps placed and renamed that
 * the world already held, the groups set, the seams and crossings set, the seams a move removed, and how many
 * commands touch anything but the layout, a map's name or the maps list (always 0).
 */
export interface LayoutReport {
  readonly gridSet: boolean;
  readonly groupsSet: readonly string[];
  readonly added: readonly { readonly id: string; readonly name: string; readonly place: MapPlace }[];
  readonly placed: readonly PlacedReport[];
  readonly renamed: readonly { readonly id: string; readonly from: string; readonly to: string }[];
  readonly grouped: readonly { readonly id: string; readonly group: string }[];
  readonly seams: readonly { readonly a: string; readonly b: string; readonly state: SeamState; readonly added: boolean }[];
  readonly crossings: readonly { readonly a: string; readonly b: string; readonly name: string; readonly added: boolean }[];
  readonly seamsRemoved: readonly string[];
  readonly layersTouched: number;
}

/**
 * The answer of `planLayout`: the commands in the order to apply them, and the report.
 */
export interface LayoutAnswer {
  readonly commands: readonly Command[];
  readonly report: LayoutReport;
}

const COLOUR = /^#[0-9a-fA-F]{6}$/;
const PLAN_COMMANDS = new Set<string>(["setGrid", "setMapGroups", "addMap", "placeMap", "setMapGroup", "renameMap", "setSeam", "removeSeam", "setCrossing"]);

/**
 * Throws a refusal naming the line of the plan.
 *
 * @param line The line, such as `maps[2] (Heath)`.
 * @param words What is wrong.
 * @throws Error always.
 */
function refuse(line: string, words: string): never {
  throw new Error(`${line}: ${words}`);
}

/**
 * Whether a value is a plain object.
 *
 * @param value Any value.
 * @returns True for a non-null, non-array object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads a list field of an object.
 *
 * @param source The object.
 * @param key The field.
 * @param line The line, for the refusal.
 * @returns The list.
 * @throws Error when the field is not a list.
 */
function listOf(source: Record<string, unknown>, key: string, line: string): readonly unknown[] {
  const value = source[key];
  if (!Array.isArray(value)) {
    refuse(line, `${key} must be a list`);
  }
  return value;
}

/**
 * Reads a whole number.
 *
 * @param value The value.
 * @param line The line, for the refusal.
 * @param field The field's name.
 * @returns The number.
 * @throws Error when the value is not a whole number.
 */
function wholeNumber(value: unknown, line: string, field: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    refuse(line, `${field} must be a whole number`);
  }
  return value;
}

/**
 * Reads a non-blank string.
 *
 * @param value The value.
 * @param line The line, for the refusal.
 * @param field The field's name.
 * @returns The string.
 * @throws Error when the value is not a string with something in it.
 */
function text(value: unknown, line: string, field: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    refuse(line, `${field} must be text`);
  }
  return value;
}

/**
 * Reads one crossing of a plan's seam.
 *
 * @param value The value.
 * @param line The seam's line.
 * @returns The crossing, with `fixed` only when true and a position only when both ends are given.
 * @throws Error when it is not a crossing.
 */
function readCrossing(value: unknown, line: string): SeamCrossing {
  if (!isRecord(value)) {
    refuse(line, "a crossing must be an object");
  }
  const name = text(value.name, line, "a crossing's name");
  const kind = value.kind;
  if (typeof kind !== "string" || !(CROSSING_KINDS as readonly string[]).includes(kind)) {
    refuse(line, `crossing ${name} is of no kind ${CROSSING_KINDS.join(", ")}`);
  }
  const hasFrom = value.from !== undefined;
  if (hasFrom !== (value.to !== undefined)) {
    refuse(line, `crossing ${name} gives only one end of its span`);
  }
  const span = hasFrom ? { from: wholeNumber(value.from, line, `crossing ${name} from`), to: wholeNumber(value.to, line, `crossing ${name} to`) } : {};
  return { name, kind: kind as SeamCrossing["kind"], ...span, ...(value.fixed === true ? { fixed: true as const } : {}) };
}

/**
 * Reads a plan from parsed JSON and checks it.
 *
 * @param value The parsed value.
 * @returns The plan.
 * @throws Error naming the line (`maps[2] (Heath)`, `seams[0] (A/B)`, `groups[1]`) and what is wrong: a field
 *   of the wrong shape, two maps on one place, a name two maps share, a group the plan does not list, a seam
 *   naming a map the plan lacks or two maps that share no edge, or one given twice.
 */
export function readLayoutPlan(value: unknown): LayoutPlan {
  if (!isRecord(value)) {
    refuse("plan", "a plan must be an object");
  }
  const gridValue = value.grid;
  if (!isRecord(gridValue)) {
    refuse("grid", "the plan needs a grid of cell, x and y");
  }
  const grid: WorldGrid = { cell: wholeNumber(gridValue.cell, "grid", "cell"), x: wholeNumber(gridValue.x, "grid", "x"), y: wholeNumber(gridValue.y, "grid", "y") };
  const groups = listOf(value, "groups", "plan").map((entry, i): MapGroup => {
    const line = `groups[${i}]`;
    if (!isRecord(entry)) {
      refuse(line, "a group must be an object");
    }
    return { id: text(entry.id, line, "id"), name: text(entry.name, line, "name"), colour: text(entry.colour, line, "colour") };
  });
  const maps = listOf(value, "maps", "plan").map((entry, i): PlanMap => {
    const name = isRecord(entry) && typeof entry.name === "string" ? ` (${entry.name})` : "";
    const line = `maps[${i}]${name}`;
    if (!isRecord(entry) || !isRecord(entry.place)) {
      refuse(line, "a map must be an object with a place");
    }
    return {
      place: { x: wholeNumber(entry.place.x, line, "place.x (a whole number of tiles)"), y: wholeNumber(entry.place.y, line, "place.y (a whole number of tiles)") },
      name: text(entry.name, line, "name"),
      ...(entry.group === undefined ? {} : { group: text(entry.group, line, "group") }),
      ...(entry.map === undefined ? {} : { map: text(entry.map, line, "map") }),
      ...(entry.confirmed === true ? { confirmed: true as const } : {}),
    };
  });
  const seams = listOf(value, "seams", "plan").map((entry, i): PlanSeam => {
    const line = `seams[${i}]`;
    if (!isRecord(entry)) {
      refuse(line, "a seam must be an object");
    }
    const state = entry.state;
    if (typeof state !== "string" || !(SEAM_STATES as readonly string[]).includes(state)) {
      refuse(line, `state must be one of ${SEAM_STATES.join(", ")}`);
    }
    return {
      a: text(entry.a, line, "a"),
      b: text(entry.b, line, "b"),
      state: state as SeamState,
      ...(entry.barrier === undefined ? {} : { barrier: text(entry.barrier, line, "barrier") }),
      crossings: listOf(entry, "crossings", line).map((crossing) => readCrossing(crossing, line)),
    };
  });
  const plan: LayoutPlan = { grid, groups, maps, seams };
  checkLayoutPlan(plan);
  return plan;
}

/**
 * Checks a plan against itself: the grid's cell, the groups' ids and colours, each map's place against every
 * other's, each name, `map` id and group, and each seam's two maps, which must share an edge on the plan's
 * grid with `a` the northern or western, and appear once.
 *
 * @param plan The plan.
 * @throws Error naming the line and what is wrong.
 */
export function checkLayoutPlan(plan: LayoutPlan): void {
  if (!Number.isInteger(plan.grid.cell) || plan.grid.cell <= 0) {
    refuse("grid", `cell ${plan.grid.cell} must be a positive whole number of tiles`);
  }
  const groupIds = new Set<string>();
  plan.groups.forEach((group, i) => {
    if (groupIds.has(group.id)) {
      refuse(`groups[${i}]`, `id ${group.id} is given twice`);
    }
    groupIds.add(group.id);
    if (!COLOUR.test(group.colour)) {
      refuse(`groups[${i}]`, `colour ${group.colour} is not of the form #rrggbb`);
    }
  });
  const places = new Map<string, number>();
  const names = new Map<string, number>();
  const ids = new Map<string, number>();
  const byName = new Map<string, PlanMap>();
  plan.maps.forEach((map, i) => {
    const line = `maps[${i}] (${map.name})`;
    if (!Number.isInteger(map.place.x) || !Number.isInteger(map.place.y)) {
      refuse(line, "place must be a whole number of tiles");
    }
    const placeKey = `${map.place.x}, ${map.place.y}`;
    const sharesPlace = places.get(placeKey);
    if (sharesPlace !== undefined) {
      refuse(line, `place ${placeKey} is also maps[${sharesPlace}]'s`);
    }
    places.set(placeKey, i);
    const nameKey = map.name.trim().toLowerCase();
    const sharesName = names.get(nameKey);
    if (sharesName !== undefined) {
      refuse(line, `name is also maps[${sharesName}]'s, ignoring case`);
    }
    names.set(nameKey, i);
    if (map.group !== undefined && !groupIds.has(map.group)) {
      refuse(line, `group ${map.group} is not one of the plan's groups`);
    }
    if (map.map !== undefined) {
      const sharesId = ids.get(map.map);
      if (sharesId !== undefined) {
        refuse(line, `map ${map.map} is also maps[${sharesId}]'s`);
      }
      ids.set(map.map, i);
    }
    byName.set(map.name, map);
  });
  const pairs = new Set<string>();
  plan.seams.forEach((seam, i) => {
    const line = `seams[${i}] (${seam.a}/${seam.b})`;
    const a = byName.get(seam.a);
    const b = byName.get(seam.b);
    if (a === undefined || b === undefined) {
      refuse(line, `map ${a === undefined ? seam.a : seam.b} is not a map of the plan`);
    }
    const dx = b.place.x - a.place.x;
    const dy = b.place.y - a.place.y;
    const cell = plan.grid.cell;
    if (!((dx === cell && dy === 0) || (dx === 0 && dy === cell))) {
      refuse(line, (dx === -cell && dy === 0) || (dx === 0 && dy === -cell) ? "a must be the northern or western map" : "the maps share no edge");
    }
    if (pairs.has(`${seam.a}/${seam.b}`)) {
      refuse(line, "the pair is given twice");
    }
    pairs.add(`${seam.a}/${seam.b}`);
    if (seam.state === "closed" && seam.crossings.length > 0) {
      refuse(line, "a closed seam holds no crossing");
    }
    const crossingNames = new Set<string>();
    for (const crossing of seam.crossings) {
      if (crossingNames.has(crossing.name)) {
        refuse(line, `crossing ${crossing.name} is given twice`);
      }
      crossingNames.add(crossing.name);
    }
  });
}

/**
 * A crossing as a command writes it: `fixed` only when true, a span only when both ends are given.
 *
 * @param crossing The crossing.
 * @returns The crossing.
 */
function plainCrossing(crossing: SeamCrossing): SeamCrossing {
  return { name: crossing.name, kind: crossing.kind, ...(crossing.from === undefined || crossing.to === undefined ? {} : { from: crossing.from, to: crossing.to }), ...(crossing.fixed === true ? { fixed: true as const } : {}) };
}

/**
 * The state `planLayout` builds as it goes: the world after the commands so far, the commands and the report's
 * lists.
 */
interface Run {
  world: WorldDocument;
  readonly commands: Command[];
}

/**
 * Applies a command to the run's world and records it, naming the plan's line when the document refuses it.
 *
 * @param run The run.
 * @param line The plan's line the command comes from.
 * @param command The command.
 * @throws Error `line: reason` when the command does not apply.
 */
function emit(run: Run, line: string, command: Command): void {
  try {
    run.world = applyCommand(run.world, command);
  } catch (error) {
    refuse(line, error instanceof Error ? error.message : String(error));
  }
  run.commands.push(command);
}

/**
 * Finds the world's map a plan map stands for: the map the plan names, else the one on its place, else the one
 * of its name, never one the plan has already given to another.
 *
 * @param world The world so far.
 * @param plan The plan map.
 * @param line The plan map's line.
 * @param taken The ids already matched or named by the plan.
 * @returns The map, or undefined for a map the world lacks.
 * @throws Error when the plan names a map the world lacks.
 */
function matchMap(world: WorldDocument, plan: PlanMap, line: string, taken: ReadonlySet<string>): MapDocument | undefined {
  if (plan.map !== undefined) {
    const named = world.maps.find((m) => m.id === plan.map);
    if (named === undefined) {
      refuse(line, `the world has no map ${plan.map}`);
    }
    return named;
  }
  const free = world.maps.filter((m) => !taken.has(m.id));
  const onPlace = free.find((m) => m.place !== undefined && depthOf(m) === 0 && m.place.x === plan.place.x && m.place.y === plan.place.y);
  return onPlace ?? free.find((m) => m.name.trim().toLowerCase() === plan.name.trim().toLowerCase());
}

/**
 * Brings the plan's groups into the world: those it lists are added or changed by id, the world's others kept.
 *
 * @param run The run.
 * @param plan The plan.
 * @returns The ids of the groups it added or changed.
 */
function setGroups(run: Run, plan: LayoutPlan): string[] {
  const held = run.world.mapGroups ?? [];
  const next = [...held];
  const set: string[] = [];
  for (const group of plan.groups) {
    const index = next.findIndex((g) => g.id === group.id);
    if (index < 0) {
      next.push(group);
      set.push(group.id);
    } else if (!sameJson(next[index], group)) {
      next[index] = group;
      set.push(group.id);
    }
  }
  if (set.length > 0) {
    emit(run, "groups", { kind: "setMapGroups", before: held, after: next });
  }
  return set;
}

/**
 * The seams of a map that would lose their shared edge if it stood at a place.
 *
 * @param world The world.
 * @param mapId The map.
 * @param place The place.
 * @returns The seams, in key order.
 */
function seamsLeftBy(world: WorldDocument, mapId: string, place: MapPlace): Seam[] {
  const moved: WorldDocument = { ...world, maps: world.maps.map((m) => (m.id === mapId ? { ...m, place } : m)) };
  return seamsOf(world, mapId).filter((seam) => {
    try {
      checkSeam(moved, seam);
      return false;
    } catch {
      return true;
    }
  });
}

/**
 * Turns a plan and a world into the commands that bring the world to the plan, and a report. A map is matched
 * by the id the plan names, then by place, then by name, so applying a plan again adds nothing. New maps are
 * added as drafts of the grid's cell on a side, the existing maps' places and names set first and the new maps
 * after them, then the seams and crossings, one command each. A seam's crossing the world holds is left as the
 * builders have it unless the plan gives it a position that differs; a crossing the plan does not name is
 * never removed. No command reads or writes a layer, a prop, a marker or a tileset.
 *
 * @param world The world; never mutated.
 * @param plan The plan.
 * @param options The tab's id tag.
 * @returns The commands in the order to apply them, and the report.
 * @throws Error naming the plan's line when the plan breaks a rule of itself or of the world: a map the world
 *   lacks, a place off the chunk lattice, over another map or on a map that holds layers and stands elsewhere
 *   (without `confirmed`), a name another map holds, or a seam the document refuses.
 */
export function planLayout(world: WorldDocument, plan: LayoutPlan, options: PlanOptions = {}): LayoutAnswer {
  checkLayoutPlan(plan);
  const run: Run = { world, commands: [] };
  const gridSet = !sameJson(world.grid, plan.grid);
  if (gridSet) {
    emit(run, "grid", { kind: "setGrid", ...(world.grid === undefined ? {} : { before: world.grid }), after: plan.grid });
  }
  const groupsSet = setGroups(run, plan);

  const ids = new Map<string, string>();
  const taken = new Set<string>(plan.maps.flatMap((m) => (m.map === undefined ? [] : [m.map])));
  const matched = plan.maps.map((planMap, i) => {
    const line = `maps[${i}] (${planMap.name})`;
    const found = matchMap(run.world, planMap, line, taken);
    if (found !== undefined) {
      taken.add(found.id);
    }
    return { planMap, line, found };
  });

  const renamed: { id: string; from: string; to: string }[] = [];
  const grouped: { id: string; group: string }[] = [];
  const placed: PlacedReport[] = [];
  const seamsRemoved: string[] = [];
  for (const { planMap, line, found } of matched) {
    if (found === undefined) {
      continue;
    }
    ids.set(planMap.name, found.id);
    const target = canonicalPlace({ x: planMap.place.x, y: planMap.place.y }) as MapPlace;
    if (!sameJson(canonicalPlace(found.place), target)) {
      if (found.place !== undefined && found.layers.length > 0 && planMap.confirmed !== true) {
        refuse(line, `map ${found.id} holds layers and stands at ${found.place.x}, ${found.place.y}; moving it to ${target.x}, ${target.y} needs confirmed`);
      }
      if (found.place !== undefined) {
        for (const seam of seamsLeftBy(run.world, found.id, target)) {
          emit(run, line, { kind: "removeSeam", seam });
          seamsRemoved.push(seamKey(seam));
        }
      }
      emit(run, line, { kind: "placeMap", mapId: found.id, ...(found.place === undefined ? {} : { before: found.place }), after: target });
      placed.push({ id: found.id, name: planMap.name, ...(found.place === undefined ? {} : { from: found.place }), to: target, moved: found.place !== undefined, leaves: [], gains: [] });
    }
    if (found.name !== planMap.name) {
      const refusal = mapNameRefusal(run.world, planMap.name, found.id);
      if (refusal !== null) {
        refuse(line, refusal);
      }
      emit(run, line, { kind: "renameMap", mapId: found.id, before: found.name, after: planMap.name });
      renamed.push({ id: found.id, from: found.name, to: planMap.name });
    }
    if (planMap.group !== undefined && planMap.group !== found.mapGroup) {
      emit(run, line, { kind: "setMapGroup", mapId: found.id, ...(found.mapGroup === undefined ? {} : { before: found.mapGroup }), after: planMap.group });
      grouped.push({ id: found.id, group: planMap.group });
    }
  }

  const added: { id: string; name: string; place: MapPlace }[] = [];
  for (const { planMap, line, found } of matched) {
    if (found !== undefined) {
      continue;
    }
    const refusal = mapNameRefusal(run.world, planMap.name);
    if (refusal !== null) {
      refuse(line, refusal);
    }
    const id = allocateMapId(run.world, options.idTag ?? null);
    const target = canonicalPlace({ x: planMap.place.x, y: planMap.place.y }) as MapPlace;
    const map = { ...newMapDocument(world.id, id, planMap.name, plan.grid.cell, plan.grid.cell), draft: true as const };
    emit(run, line, { kind: "addMap", index: run.world.maps.length, map });
    emit(run, line, { kind: "placeMap", mapId: id, after: target });
    if (planMap.group !== undefined) {
      emit(run, line, { kind: "setMapGroup", mapId: id, after: planMap.group });
    }
    ids.set(planMap.name, id);
    added.push({ id, name: planMap.name, place: target });
  }

  const seams: { a: string; b: string; state: SeamState; added: boolean }[] = [];
  const crossings: { a: string; b: string; name: string; added: boolean }[] = [];
  plan.seams.forEach((planSeam, i) => {
    const line = `seams[${i}] (${planSeam.a}/${planSeam.b})`;
    const a = ids.get(planSeam.a) as string;
    const b = ids.get(planSeam.b) as string;
    const head: SeamHead = { state: planSeam.state, ...(planSeam.barrier === undefined ? {} : { barrier: planSeam.barrier }) };
    const held = seamOf(run.world, a, b);
    if (held === undefined) {
      emit(run, line, { kind: "setSeam", a, b, after: head });
      seams.push({ a, b, state: head.state, added: true });
    } else if (!sameJson(seamHead(held), head)) {
      emit(run, line, { kind: "setSeam", a, b, before: seamHead(held), after: head });
      seams.push({ a, b, state: head.state, added: false });
    }
    for (const crossing of planSeam.crossings) {
      const after = plainCrossing(crossing);
      const current = seamOf(run.world, a, b)?.crossings.find((c) => c.name === crossing.name);
      if (current === undefined) {
        emit(run, line, { kind: "setCrossing", a, b, after });
        crossings.push({ a, b, name: crossing.name, added: true });
      } else if (after.fixed === true && after.from !== undefined && !sameJson(current, after)) {
        emit(run, line, { kind: "setCrossing", a, b, before: current, after });
        crossings.push({ a, b, name: crossing.name, added: false });
      }
    }
  });

  const reported = placed.map((entry) => {
    if (!entry.moved) {
      return entry;
    }
    const before = new Set(neighboursOf(world, entry.id).map((n) => n.map));
    const after = new Set(neighboursOf(run.world, entry.id).map((n) => n.map));
    return { ...entry, leaves: [...before].filter((id) => !after.has(id)), gains: [...after].filter((id) => !before.has(id)) };
  });
  return {
    commands: run.commands,
    report: {
      gridSet,
      groupsSet,
      added,
      placed: reported,
      renamed,
      grouped,
      seams,
      crossings,
      seamsRemoved,
      layersTouched: run.commands.filter((c) => !PLAN_COMMANDS.has(c.kind)).length,
    },
  };
}
